import { getTranslations } from 'next-intl/server';
import { isStandardLoyaltyLocale, Locale, ReviewsSortOption } from '@headless-workspace/config';
import { UserSephoraCard, UserSephoraCardUnlimited } from '@headless-workspace/domain/common/server';
import { RatingsAndReviewsDTO, reviewDTO } from './RatingsAndReviewsDTO';

export enum ReviewPurchaserType {
    VERIFIED = 'verified',
    TESTER = 'tester',
    UNCONTROLLED = 'uncontrolled',
    SPONTANEOUS = 'spontaneous',
}

export type ReviewSortOption = {
    value: string;
    label: string;
    tag: ReviewsSortOption;
};

export type RatingFilterData = {
    ratingValue: number;
    reviewsCountPerRating: number;
    progressValue: number;
};

export type RatingsAndReviewsValue = {
    reviewCount: number;
    ratingFilter: RatingFilterData[];
    sortingOption: ReviewSortOption[];
    reviews: ProductReview[];
};

export type RatingsAndReviewsData = {
    title: string;
    nickName: string;
    content: string;
    rating: number;
    isRecommended: boolean;
    age: string;
    usageDuration: string;
    incentivizedReview: boolean;
    acceptedTermsAndConditions: boolean;
};

export type ProductReview = {
    id?: string;
    rating: number;
    createdAt: Date;
    userName: string;
    userGender?: string;
    userAge?: string;
    userCard?: UserSephoraCard | UserSephoraCardUnlimited;
    purchaserType: ReviewPurchaserType;
    purchaseProductSince?: string;
    title: string;
    content?: string;
    language: string;
    isRecommended?: boolean;
    source?: {
        label: string;
        href: string;
    };
    vote: {
        like: number;
        dislike: number;
    };
};

const findUserInfo = (data: reviewDTO['contextualData'], key: string): string | undefined => {
    const getObj = data?.find((item) => item.id === key);
    return getObj?.label;
};

const mapLoyalty = (
    loyalty: reviewDTO['loyaltyStatus'],
    locale: Locale,
): UserSephoraCard | UserSephoraCardUnlimited | undefined => {
    const hasLoyalty = loyalty !== UserSephoraCard.PreBuy && loyalty !== 'NONE';

    if (!hasLoyalty) {
        return undefined;
    }

    return isStandardLoyaltyLocale(locale) ? (loyalty as UserSephoraCard) : (loyalty as UserSephoraCardUnlimited);
};

const mapReview = (ProductReview: reviewDTO, locale: Locale): ProductReview => ({
    id: ProductReview.id,
    rating: ProductReview.rating,
    createdAt: new Date(ProductReview.createdAt),
    userName: ProductReview.userName ?? '',
    userGender: findUserInfo(ProductReview.contextualData, 'Gender'),
    userAge: findUserInfo(ProductReview.contextualData, 'Age'),
    userCard: mapLoyalty(ProductReview.loyaltyStatus, locale),
    purchaserType:
        ReviewPurchaserType[ProductReview.badge as keyof typeof ReviewPurchaserType] ??
        ReviewPurchaserType.UNCONTROLLED,
    purchaseProductSince: findUserInfo(ProductReview.contextualData, 'ExperienceDachat'),
    title: ProductReview.title ?? '',
    content: ProductReview.body ?? '',
    language: ProductReview.locale ?? '',
    isRecommended: ProductReview.isRecommended,
    source: ProductReview.syndication
        ? {
              label: ProductReview.syndication?.name ?? '',
              href: ProductReview.syndication?.url ?? '',
          }
        : undefined,
    vote: {
        like: ProductReview.helpfulness.positiveCount,
        dislike: ProductReview.helpfulness.negativeCount,
    },
});

export const mapRatingFilter = (
    ratingFilter: RatingsAndReviewsDTO['distribution'],
    count: number,
): RatingFilterData[] => {
    return [5, 4, 3, 2, 1].map((rating) => {
        const found = ratingFilter.find((item) => item.rating === rating);
        const reviewsCountPerRating = found?.count ?? 0;

        return {
            ratingValue: rating,
            reviewsCountPerRating,
            progressValue: count > 0 ? (reviewsCountPerRating / count) * 100 : 0,
        };
    });
};

const priorityOrder: ReviewsSortOption[] = [
    ReviewsSortOption.MostRecent,
    ReviewsSortOption.LeastRecent,
    ReviewsSortOption.BestRated,
    ReviewsSortOption.WorstRated,
];

const sortingOptionsId = Object.values(ReviewsSortOption);
const isIdValid = (id: string): id is ReviewsSortOption => sortingOptionsId.some((optionId) => optionId === id);

export const mapSortingOption = async (sorting: { id: string; label: string }[]): Promise<ReviewSortOption[]> => {
    const t = await getTranslations('Discover.Pdp.ratingAndReviews');

    const sortingOptionLabels = {
        [ReviewsSortOption.MostRecent]: t('label.mostRecent'),
        [ReviewsSortOption.LeastRecent]: t('label.leastRecent'),
        [ReviewsSortOption.BestRated]: t('label.bestRated'),
        [ReviewsSortOption.WorstRated]: t('label.worstRated'),
    } as const;

    const validOptions = sorting.filter(({ id }) => isIdValid(id));

    const result: ReviewSortOption[] = priorityOrder
        .map((id) => {
            const option = validOptions.find((opt) => opt.id === id);
            if (!option) return null;

            const label = isIdValid(option.id) ? sortingOptionLabels[option.id] : '';

            return {
                label,
                tag: option.id as ReviewsSortOption,
                value: label,
            };
        })
        .filter((item): item is ReviewSortOption => item !== null);

    return result;
};

export const mapRatingsAndReviews = async (
    dto: RatingsAndReviewsDTO,
    locale: Locale,
): Promise<RatingsAndReviewsValue> => {
    return {
        reviewCount: dto.count,
        ratingFilter: mapRatingFilter(dto.distribution, dto.count),
        sortingOption: await mapSortingOption(dto.sorting),
        reviews: dto.reviews.map((review) => mapReview(review, locale)),
    };
};
